import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

export const CV_TOUR_SEEN_KEY = "hr_cv_tour_seen_v3";

const TOOLTIP_HEIGHT = 320;
const TOUR_STEP_DELAY_MS = 220;
const TARGET_RETRY_COUNT = 8;
const TARGET_RETRY_DELAY_MS = 120;
const STYLE_TAG_ID = "cv-product-tour-styles";

const BASE_STEPS = [
  {
    id: "hero",
    target: '[data-tour="cv-hero"]',
    title: "Welcome To Your CV Workspace",
    description: "This top section shows your CV status and key actions.",
    details: [
      "Check your profile title and status chips.",
      "Use this area to understand if your CV is editable or already submitted.",
    ],
  },
  {
    id: "help-replay",
    target: '[data-tour="cv-help-replay"]',
    title: "Replay Guide Anytime",
    description: "Use this ? button whenever you want to open the CV guide again.",
    details: [
      "The guide can be replayed at any time.",
      "Use it when you need a quick reminder before editing or submitting.",
    ],
  },
  {
    id: "status",
    target: '[data-tour="cv-status-chips"]',
    title: "Understand CV Status Chips",
    description: "Status chips tell you if your profile is draft, submitted, or locked.",
    details: [
      "Draft means you can continue editing.",
      "Submitted or locked means contact HR for changes.",
    ],
  },
  {
    id: "editing-state",
    target: '[data-tour="cv-editing-note"]',
    title: "Editable vs Locked Behavior",
    description: ({ canEdit, locked }) => {
      if (locked) return "Your CV is locked because it has already been submitted.";
      if (canEdit) return "Your CV is editable now. You can save draft or submit when ready.";
      return "Your CV is currently read-only because the editing window is closed.";
    },
    details: ({ canEdit, locked }) => {
      if (locked) {
        return [
          "Save Draft and Submit actions are disabled while locked.",
          "Contact HR if you need the CV reopened for corrections.",
        ];
      }

      if (canEdit) {
        return [
          "Save Draft stores changes and keeps your CV editable.",
          "Submit CV sends your profile to HR and locks editing.",
          "Submit only after all required sections are complete.",
        ];
      }

      return [
        "Save Draft and Submit are unavailable when the window is closed.",
        "Ask HR to reopen the window to continue editing.",
      ];
    },
  },
  {
    id: "completion",
    target: '[data-tour="cv-completion"]',
    title: "Track Completion Progress",
    description: "Use the completion bar as your checklist target before submitting.",
    details: [
      "Aim for all required sections completed.",
      "Low completion means some required fields or rows are still missing.",
    ],
  },
  {
    id: "tabs",
    target: '[data-tour="cv-tabs"]',
    title: "Navigate With Tabs",
    description: "Switch between all CV sections using these tabs.",
    details: [
      "Overview gives section-level completion cards.",
      "Profile and list tabs are where you fill actual data.",
    ],
  },
  {
    id: "overview-grid",
    target: '[data-tour="cv-overview-grid"]',
    title: "Overview Cards",
    description: "Each card represents a required section and its completion state.",
    details: [
      "Click any card to jump directly to that section.",
      "Minimum required rows: Experience (1), Education (1), Certificates (1).",
      "Complete all cards before final submit.",
    ],
    tab: 0,
  },
  {
    id: "profile-tab",
    target: '[data-tour="cv-tab-profile"]',
    title: "Open Profile First",
    description: "Start with Profile because it contains core required identity fields.",
    details: [
      "Profile completion affects overall readiness.",
      "Missing profile values will block submit.",
    ],
  },
  {
    id: "contact-fields",
    target: '[data-tour="cv-contact-section"]',
    title: "Fill Contact Fields Clearly",
    description: "Enter accurate Full Name, Email, and Phone.",
    details: [
      "Use a valid email format.",
      "These fields are mandatory for submission.",
    ],
    tab: 1,
  },
  {
    id: "role-fields",
    target: '[data-tour="cv-role-section"]',
    title: "Complete Role And Summary",
    description: "Provide position, years of experience, and a concise professional summary.",
    details: [
      "Years of experience must be a valid number.",
      "Summary should highlight strengths and achievements.",
    ],
    tab: 1,
  },
  {
    id: "tips",
    target: '[data-tour="cv-tips-card"]',
    title: "Use Inline Tips",
    description: "These tips list the minimum required fields for each section.",
    details: [
      "Each section needs at least one item before submission.",
      "Experience: Company and Position.",
      "Education: Institution.",
      "Language: Language and Proficiency.",
      "Certificate: Title and Issuer.",
    ],
  },
  {
    id: "add-experience",
    target: '[data-tour="cv-add-item"]',
    title: "Add Work Experience",
    description: "Use Add to create your first experience record.",
    details: [
      "At least one experience row is required.",
      "Complete required fields before saving.",
    ],
    tab: 2,
    requiresEdit: true,
  },
  {
    id: "add-education",
    target: '[data-tour="cv-add-item"]',
    title: "Add Education",
    description: "Switch to Education and add at least one valid education record.",
    details: [
      "Institution is mandatory.",
      "Add latest education first for clarity.",
    ],
    tab: 3,
    requiresEdit: true,
  },
  {
    id: "add-language",
    target: '[data-tour="cv-add-item"]',
    title: "Add Languages",
    description: "Add at least one language with proficiency level.",
    details: [
      "Language and proficiency are required together.",
      "Avoid duplicate language entries.",
    ],
    tab: 4,
    requiresEdit: true,
  },
  {
    id: "add-certificate",
    target: '[data-tour="cv-add-item"]',
    title: "Add Certificates",
    description: "Add your key certifications before final submission.",
    details: [
      "Title and issuer are required.",
      "Use recent and relevant certifications.",
    ],
    tab: 5,
    requiresEdit: true,
  },
  {
    id: "save",
    target: '[data-tour="cv-save-draft"]',
    title: "Save Draft Frequently",
    description: "Save draft at any time to prevent data loss.",
    details: [
      "Save after each section update.",
      "Saving does not lock your CV.",
    ],
    requiresEdit: true,
  },
  {
    id: "submit",
    target: '[data-tour="cv-submit-cv"]',
    title: "Submit When Fully Complete",
    description: "Submit only after all required sections are valid.",
    details: [
      "Required minimum: Experience (at least 1), Education (at least 1), Certificates (at least 1).",
      "Submit locks editing and sends your CV for HR review.",
      "If submit fails, follow the validation message and fix missing fields.",
    ],
    requiresEdit: true,
  },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function getTooltipPosition(targetEl) {
  const rect = targetEl.getBoundingClientRect();
  const tooltipWidth = window.innerWidth < 640 ? window.innerWidth - 32 : 430;
  const left = clamp(rect.left, 16, window.innerWidth - tooltipWidth - 16);
  const defaultTop = rect.bottom + 14;
  const showAbove = defaultTop + TOOLTIP_HEIGHT > window.innerHeight - 16;
  const top = showAbove
    ? Math.max(16, rect.top - TOOLTIP_HEIGHT - 14)
    : defaultTop;
  const placement = showAbove ? "top" : "bottom";
  const targetCenter = rect.left + rect.width / 2;
  const arrowLeft = clamp(targetCenter - left - 8, 14, tooltipWidth - 22);

  return { top, left, width: tooltipWidth, placement, arrowLeft };
}

export default function CvProductTour({
  isReady,
  canEdit,
  locked,
  forceStart,
  restartSignal = 0,
  setTab,
  onFinish,
}) {
  const steps = useMemo(
    () => BASE_STEPS.filter((step) => !(step.requiresEdit && !canEdit)),
    [canEdit]
  );

  const [running, setRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetEl, setTargetEl] = useState(null);
  const timersRef = useRef([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  }, []);

  useEffect(() => {
    if (document.getElementById(STYLE_TAG_ID)) return undefined;

    const style = document.createElement("style");
    style.id = STYLE_TAG_ID;
    style.textContent = `
      @keyframes cvTourPulse {
        0% { box-shadow: 0 0 0 0 rgba(66, 165, 245, 0.45); }
        70% { box-shadow: 0 0 0 14px rgba(66, 165, 245, 0); }
        100% { box-shadow: 0 0 0 0 rgba(66, 165, 245, 0); }
      }
      @keyframes cvTourPopIn {
        0% { transform: translateY(8px) scale(0.98); opacity: 0; }
        100% { transform: translateY(0) scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }, []);

  const finishTour = useCallback(
    (markSeen = true) => {
      clearTimers();
      if (markSeen) localStorage.setItem(CV_TOUR_SEEN_KEY, "1");
      setRunning(false);
      setTargetEl(null);
      onFinish?.();
    },
    [clearTimers, onFinish]
  );

  useEffect(() => () => clearTimers(), [clearTimers]);

  useEffect(() => {
    if (!isReady) return;
    const seen = localStorage.getItem(CV_TOUR_SEEN_KEY) === "1";
    if (forceStart || restartSignal > 0 || !seen) {
      setRunning(true);
      setStepIndex(0);
    }
  }, [forceStart, restartSignal, isReady]);

  const currentStep = steps[stepIndex];
  const stepContext = useMemo(() => ({ canEdit, locked }), [canEdit, locked]);
  const resolvedCurrentStep = useMemo(() => {
    if (!currentStep) return null;
    const resolvedDescription =
      typeof currentStep.description === "function"
        ? currentStep.description(stepContext)
        : currentStep.description;
    const resolvedDetails =
      typeof currentStep.details === "function"
        ? currentStep.details(stepContext)
        : currentStep.details;
    return {
      ...currentStep,
      description: resolvedDescription,
      details: resolvedDetails,
    };
  }, [currentStep, stepContext]);

  useEffect(() => {
    if (!running || !currentStep) return;

    if (typeof currentStep.tab === "number") {
      setTab(currentStep.tab);
    }

    clearTimers();

    const resolveTarget = (attempt = 0) => {
      const el = document.querySelector(currentStep.target);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        setTargetEl(el);
        return;
      }

      if (attempt < TARGET_RETRY_COUNT) {
        const retryId = window.setTimeout(
          () => resolveTarget(attempt + 1),
          TARGET_RETRY_DELAY_MS
        );
        timersRef.current.push(retryId);
        return;
      }

      if (stepIndex < steps.length - 1) {
        setStepIndex((prev) => prev + 1);
      } else {
        finishTour(true);
      }
    };

    const startId = window.setTimeout(() => resolveTarget(0), TOUR_STEP_DELAY_MS);
    timersRef.current.push(startId);
  }, [clearTimers, currentStep, finishTour, running, setTab, stepIndex, steps.length]);

  useEffect(() => {
    if (!running || !targetEl) return;

    const prev = {
      outline: targetEl.style.outline,
      outlineOffset: targetEl.style.outlineOffset,
      position: targetEl.style.position,
      zIndex: targetEl.style.zIndex,
      animation: targetEl.style.animation,
      boxShadow: targetEl.style.boxShadow,
      borderRadius: targetEl.style.borderRadius,
    };

    targetEl.style.outline = "3px solid rgba(144, 202, 249, 0.98)";
    targetEl.style.outlineOffset = "3px";
    targetEl.style.zIndex = "1301";
    targetEl.style.animation = "cvTourPulse 1.6s ease-out infinite";
    targetEl.style.boxShadow = "0 0 0 8px rgba(33, 150, 243, 0.16)";
    if (!targetEl.style.borderRadius) targetEl.style.borderRadius = "8px";
    if (!prev.position || prev.position === "static") {
      targetEl.style.position = "relative";
    }

    return () => {
      targetEl.style.outline = prev.outline;
      targetEl.style.outlineOffset = prev.outlineOffset;
      targetEl.style.position = prev.position;
      targetEl.style.zIndex = prev.zIndex;
      targetEl.style.animation = prev.animation;
      targetEl.style.boxShadow = prev.boxShadow;
      targetEl.style.borderRadius = prev.borderRadius;
    };
  }, [running, targetEl]);

  if (!running || !resolvedCurrentStep || !targetEl) return null;

  const pos = getTooltipPosition(targetEl);
  const progress = ((stepIndex + 1) / Math.max(steps.length, 1)) * 100;

  return (
    <>
      <Backdrop
        open
        sx={{
          zIndex: 1300,
          bgcolor: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(1px)",
        }}
      />

      <Paper
        key={resolvedCurrentStep.id}
        elevation={8}
        sx={{
          position: "fixed",
          zIndex: 1302,
          top: pos.top,
          left: pos.left,
          width: pos.width,
          borderRadius: 2,
          p: 2,
          animation: "cvTourPopIn 220ms ease-out",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 12,
            height: 12,
            bgcolor: "background.paper",
            transform: "rotate(45deg)",
            left: pos.arrowLeft,
            ...(pos.placement === "bottom" ? { top: -6 } : { bottom: -6 }),
          }}
        />

        <Stack spacing={1.25}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="overline" sx={{ fontWeight: 900, color: "text.secondary" }}>
              CV Product Tour
            </Typography>
            <Chip
              size="small"
              label={`Step ${stepIndex + 1}/${steps.length}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
          </Stack>

          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
            {resolvedCurrentStep.title}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {resolvedCurrentStep.description}
          </Typography>

          {!!resolvedCurrentStep.details?.length && (
            <Box component="ul" sx={{ m: 0, pl: 2.2 }}>
              {resolvedCurrentStep.details.map((detail, idx) => (
                <Box key={`${resolvedCurrentStep.id}-d-${idx}`} component="li" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {detail}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ pt: 0.25 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 6, borderRadius: 99 }}
            />
          </Box>

          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button
              size="small"
              variant="outlined"
              onClick={() => setStepIndex((prev) => Math.max(prev - 1, 0))}
              disabled={stepIndex === 0}
            >
              Back
            </Button>

            <Stack direction="row" spacing={1}>
              <Button size="small" variant="text" onClick={() => finishTour(true)}>
                Skip
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  if (stepIndex >= steps.length - 1) {
                    finishTour(true);
                    return;
                  }
                  setStepIndex((prev) => prev + 1);
                }}
              >
                {stepIndex >= steps.length - 1 ? "Finish" : "Next"}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </>
  );
}
